import type React from "react";

/**
 * Open the browser's date picker from a date field.
 *
 * On a click anywhere in the field, since by default only the small calendar
 * icon opens it, which leaves the therapist typing the date out by hand. On
 * focus only when the keyboard brought it there: a dialog focuses its first
 * field as it opens, and after a tap on a phone that would throw the picker
 * over the dialog before the therapist has even seen it.
 *
 * `showPicker` throws when not called from a user gesture and is absent on
 * older browsers; both fall back to the field's own behaviour.
 */
export function openDatePicker(
    event:
        | React.MouseEvent<HTMLInputElement>
        | React.FocusEvent<HTMLInputElement>
): void {
    if (event.type === "focus" && !lastInputWasKeyboard()) return;
    try {
        event.currentTarget.showPicker?.();
    } catch {
        // Left to the browser: the field is still typeable.
    }
}

/**
 * Whether the most recent interaction was a key press rather than a tap or a
 * click. `:focus-visible` cannot answer this: Chrome also grants it to focus
 * a script moves, as a dialog does when it opens.
 */
let keyboardLast = false;

if (typeof document !== "undefined") {
    document.addEventListener("keydown", () => (keyboardLast = true), true);
    document.addEventListener("pointerdown", () => (keyboardLast = false), true);
}

function lastInputWasKeyboard(): boolean {
    return keyboardLast;
}
